import React, { type ReactNode, createContext, useCallback } from 'react';
import noop from 'lodash/noop';
import { NAVIGATION_ITEM } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/action-subject-type.tsx';
import {
	CUSTOM_ITEM,
	CUSTOM_ITEM_ACTION,
	FIXED_ITEM,
	FIXED_ITEM_BUTTON,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import {
	LEVEL_ONE,
	LEVEL_TWO,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import { MENU_ID_ANALYTIC_EVENT_IDS } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/side-nav.tsx';
import type { StarType } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/star.tsx';
import { MENU_ID_STARRED } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { JiraFavouriteType } from '../../../ui/starred-menu/__generated__/StarredMenuQuery.graphql';
import { FILTER, type FixItemTypeId } from '../../constants.tsx';

type AnalyticContextType = {
	fireUIAnalyticForFixItem({ itemId }: { itemId: FixItemTypeId }): void;
	fireUIAnalyticForFilterButton(): void;
	fireUIAnalyticForFilterActions({ itemId }: { itemId: string }): void;
	fireUIAnalyticForStarredItems({
		itemId,
		itemType,
	}: {
		itemId: string;
		itemType: JiraFavouriteType;
	}): void;
	fireUIAnalyticForStarrButton({
		starType,
		parentItemId,
		parentItemType,
	}: {
		starType: StarType;
		parentItemId: string;
		parentItemType: JiraFavouriteType;
	}): void;
};

export const AnalyticContext = createContext<AnalyticContextType>({
	fireUIAnalyticForFixItem: noop,
	fireUIAnalyticForFilterButton: noop,
	fireUIAnalyticForFilterActions: noop,
	fireUIAnalyticForStarredItems: noop,
	fireUIAnalyticForStarrButton: noop,
});

export const AnalyticContextProvider = ({
	children,
}: {
	children: ReactNode;
}): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireUIAnalyticForFixItem = useCallback<AnalyticContextType['fireUIAnalyticForFixItem']>(
		({ itemId }) => {
			fireUIAnalytics(createAnalyticsEvent({}), {
				action: 'clicked',
				actionSubject: NAVIGATION_ITEM,
				actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_STARRED],
				attributes: {
					level: LEVEL_ONE,
					componentType: FIXED_ITEM,
					itemId,
				},
			});
		},
		[createAnalyticsEvent],
	);

	const fireUIAnalyticForFilterButton = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_STARRED],
			attributes: {
				level: LEVEL_ONE,
				componentType: FIXED_ITEM_BUTTON,
				itemId: FILTER,
			},
		});
	}, [createAnalyticsEvent]);

	const fireUIAnalyticForFilterActions = useCallback<
		AnalyticContextType['fireUIAnalyticForFilterActions']
	>(
		({ itemId }) => {
			fireUIAnalytics(createAnalyticsEvent({}), {
				action: 'clicked',
				actionSubject: 'dropdownItem',
				actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_STARRED],
				attributes: {
					level: LEVEL_TWO,
					componentType: FIXED_ITEM_BUTTON,
					itemId,
					parentItemId: FILTER,
				},
			});
		},
		[createAnalyticsEvent],
	);

	const fireUIAnalyticForStarredItems = useCallback<
		AnalyticContextType['fireUIAnalyticForStarredItems']
	>(
		({ itemId, itemType }) => {
			fireUIAnalytics(createAnalyticsEvent({}), {
				action: 'clicked',
				actionSubject: NAVIGATION_ITEM,
				actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_STARRED],
				attributes: {
					level: LEVEL_ONE,
					componentType: CUSTOM_ITEM,
					itemType: itemType.toLowerCase(),
					itemId,
				},
			});
		},
		[createAnalyticsEvent],
	);

	const fireUIAnalyticForStarrButton = useCallback<
		AnalyticContextType['fireUIAnalyticForStarrButton']
	>(
		({ starType, parentItemId, parentItemType }) => {
			fireUIAnalytics(createAnalyticsEvent({}), {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_STARRED],
				attributes: {
					level: LEVEL_TWO,
					componentType: CUSTOM_ITEM_ACTION,
					itemId: starType,
					parentItemId,
					parentItemType: parentItemType.toLowerCase(),
				},
			});
		},
		[createAnalyticsEvent],
	);

	return (
		<AnalyticContext.Provider
			value={{
				fireUIAnalyticForFixItem,
				fireUIAnalyticForFilterButton,
				fireUIAnalyticForFilterActions,
				fireUIAnalyticForStarredItems,
				fireUIAnalyticForStarrButton,
			}}
		>
			{children}
		</AnalyticContext.Provider>
	);
};
